import React from 'react'
import Topbar from '../layouts/Topbar'
import Sidebar from '../layouts/Sidebar'
import axios from 'axios';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { BeatLoader } from 'react-spinners';
import { FaBuilding, FaPlus, FaEdit, FaTrash, FaSearch, FaEnvelope, FaIdCard, FaCalendarAlt, FaTimes, FaToggleOn, FaToggleOff, FaCheckCircle, FaTimesCircle, FaPhone, FaCity, FaHashtag, FaPercent, FaClock, FaUserTie, FaCreditCard, FaCamera, FaFilter } from 'react-icons/fa';
import './Companies.css';

const Companies = () => {
    const navigate = useNavigate();
    const baseUrl = "https://api.burrayhr.com";
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [subscriptionFilter, setSubscriptionFilter] = useState('all');

    useEffect(() => {
        // Check authentication and role
        const checkAuth = () => {
            const token = localStorage.getItem('token');
            const userRole = localStorage.getItem('userRole');

            if (!token) {
                Swal.fire({
                    icon: 'error',
                    title: 'Unauthorized',
                    text: 'Please login to continue'
                });
                navigate('/login');
                return false;
            }

            if (userRole !== 'overall_manager') {
                Swal.fire({
                    icon: 'error',
                    title: 'Forbidden',
                    text: 'You do not have permission to access this page'
                });
                navigate('/overall-manager/dashboard');
                return false;
            }

            return true;
        };

        const fetchCompaniesData = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${baseUrl}/api/companies`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                setCompanies(response.data);
                setLoading(false);
            } catch (err) {
                if (err.response?.status === 401) {
                    localStorage.removeItem('token');
                    localStorage.removeItem('userRole');
                    navigate('/login');
                } else {
                    setError(err.message);
                }
                setLoading(false);
            }
        };

        if (checkAuth()) {
            fetchCompaniesData();
        }
    }, [navigate, baseUrl]);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleStatusFilterChange = (e) => {
        setStatusFilter(e.target.value);
    };

    const handleSubscriptionFilterChange = (e) => {
        setSubscriptionFilter(e.target.value);
    };

    const filteredCompanies = companies.filter(company =>
        company && company.name ? 
        company.name.toLowerCase().includes(searchTerm.toLowerCase()) : 
        false
    );

    // Handle company status toggle
    const handleToggleStatus = async (company, e) => {
        if (e) e.stopPropagation();
        
        const newStatus = company.is_subscribed ? false : true;
        const actionText = newStatus ? 'activate' : 'deactivate';
        
        try {
            const result = await Swal.fire({
                title: `${newStatus ? 'Activate' : 'Deactivate'} Company?`,
                text: `Are you sure you want to ${actionText} ${company.name}?`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: newStatus ? '#10b981' : '#ef4444',
                cancelButtonColor: '#64748b',
                confirmButtonText: `Yes, ${actionText} it!`
            });

            if (result.isConfirmed) {
                const token = localStorage.getItem('token');
                const response = await axios.patch(`${baseUrl}/api/companies/${company.id}/`, 
                    { is_subscribed: newStatus },
                    {
                        headers: { 'Authorization': `Bearer ${token}` }
                    }
                );

                // Update the company in the state
                const updatedCompany = { ...company, is_subscribed: newStatus };
                setCompanies(companies.map(c => 
                    c.id === company.id ? updatedCompany : c
                ));
                
                // Update selected company if it's the one being toggled
                if (selectedCompany && selectedCompany.id === company.id) {
                    setSelectedCompany(updatedCompany);
                }

                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: `Company ${newStatus ? 'activated' : 'deactivated'} successfully.`,
                    confirmButtonColor: '#1e3a8a'
                });
            }
        } catch (error) {
            console.log('Toggle status error:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: `Failed to ${actionText} company.`,
                confirmButtonColor: '#1e3a8a'
            });
        }
    };

    const handleAddCompany = () => {
        Swal.fire({
            title: 'Add New Company',
            html: `
                <input type="text" id="companyName" class="" placeholder="Company Name">
                <input type="email" id="companyEmail" class="" placeholder="Company Email">
                <input type="tel" id="companyPhone" class="" placeholder="Phone Number" value="+263">
                <textarea id="companyAddress" class="swal2-textarea" placeholder="Company Address"></textarea>
            `,
            showCancelButton: true,
            confirmButtonText: 'Add',
            confirmButtonColor: '#1e3a8a',
            cancelButtonColor: '#64748b',
            showLoaderOnConfirm: true,
            didOpen: () => {
                // Add event listener to ensure phone always has +263 prefix
                const phoneInput = document.getElementById('companyPhone');
                phoneInput.addEventListener('input', function(e) {
                    if (!this.value.startsWith('+263')) {
                        this.value = '+263' + this.value.replace(/^\+263/, '');
                    }
                });
            },
            preConfirm: () => {
                const name = document.getElementById('companyName').value;
                const email = document.getElementById('companyEmail').value;
                const phone = document.getElementById('companyPhone').value;
                const address = document.getElementById('companyAddress').value;
                
                if (!name || !email) {
                    Swal.showValidationMessage('Please enter company name and email');
                    return false;
                }
                
                // Validate email format
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(email)) {
                    Swal.showValidationMessage('Please enter a valid email address');
                    return false;
                }
                
                // Validate phone format
                if (!phone.startsWith('+263') || phone.length < 7) {
                    Swal.showValidationMessage('Please enter a valid phone number with +263 country code');
                    return false;
                }
                
                return { name, email, phone, address };
            }
        }).then((result) => {
            if (result.isConfirmed) {
                addCompany(result.value);
            }
        });
    };

    const addCompany = async (companyData) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${baseUrl}/api/companies/`, companyData, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            
            if (response.data && response.data.name) {
                setCompanies([...companies, response.data]);
                
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Company added successfully',
                    confirmButtonColor: '#1e3a8a'
                });
            } else {
                throw new Error('Invalid response data');
            }
        } catch (error) {
            console.error('Add company error:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to add company',
                confirmButtonColor: '#1e3a8a'
            });
        }
    };

    const handleEditCompany = (company, e) => {
        if (e) e.stopPropagation();
        
        Swal.fire({
            title: 'Edit Company',
            html: `
                <div style="display: flex; flex-direction: column; gap: 10px; overflow-x: hidden; overflow-y: auto; max-height: 80vh; padding: 10px;" class="edit-company-form">
                    <div class="form-group logo-upload-group">
                        <label for="companyLogo">Company Logo</label>
                        <div class="logo-upload-container">
                            <div class="logo-preview-container" id="logoPreviewContainer">
                                ${company.logo ? 
                                    `<img style="width: 100px; height: 100px;" src="${baseUrl}${company.logo}" alt="Company Logo" class="logo-preview" id="logoPreview" />` :
                                    `<div class="logo-placeholder" id="logoPlaceholder"><FaBuilding /></div>`
                                }
                                <div class="logo-upload-overlay">
                                    <FaCamera className="upload-icon" />
                                    <span>Change Logo</span>
                                </div>
                            </div>
                            <input 
                                type="file" 
                                id="companyLogo" 
                                class="logo-input" 
                                accept="image/*"
                            />
                            <div class="logo-upload-info">
                                <small class="input-help">Click to upload or change logo</small>
                                <small class="file-info">Recommended size: 200x200px</small>
                            </div>
                            
                        </div>
                    </div>
                    <div class="form-group">
                        <label for="companyName">Company Name</label>
                        <input type="text" id="companyName" class="" placeholder="Enter company name" value="${company.name || ''}">
                    </div>
                    <div class="form-group">
                        <label for="companyEmail">Email Address</label>
                        <input type="email" id="companyEmail" placeholder="Enter company email" value="${company.email || ''}">
                    </div>
                    <div class="form-group">
                        <label for="companyPhone">Phone Number</label>
                        <input type="tel" id="companyPhone" class="" placeholder="Enter phone number" value="${company.phone_number || '+263'}">
                    </div>
                    <div class="form-group">
                        <label for="companyCity">City</label>
                        <input type="text" id="companyCity" class="" placeholder="Enter city" value="${company.city || ''}">
                    </div>
                    <div class="form-group">
                        <label for="companyAddress">Address</label>
                        <textarea id="companyAddress" class="" placeholder="Enter company address">${company.address || ''}</textarea>
                    </div>
                    <div class="form-group">
                        <label for="employeeIdPrefix">Employee ID Prefix</label>
                        <input type="text" id="employeeIdPrefix" class="" placeholder="Enter employee ID prefix" value="${company.employee_id_prefix || ''}">
                    </div>
                    <div class="form-group">
                        <label for="mdEmail">Managing Director Email</label>
                        <input type="email" id="mdEmail" class="" placeholder="Enter MD email" value="${company.md_email || ''}">
                    </div>
                    <div class="form-group">
                        <label for="gratuityIncrementType">Gratuity Increment Type</label>
                        <select id="gratuityIncrementType" class="">
                            <option value="fixed" ${company.gratuity_increment_type === 'fixed' ? 'selected' : ''}>Fixed</option>
                            <option value="incremental" ${company.gratuity_increment_type === 'incremental' ? 'selected' : ''}>Incremental</option>
                        </select>
                    </div>
                    <div class="form-group" id="gratuityStartYearsGroup" style="display: ${company.gratuity_increment_type === 'incremental' ? 'block' : 'none'}">
                        <label for="gratuityStartYears">Gratuity Starting Years</label>
                        <select id="gratuityStartYears" class="">
                            <option value="3" ${company.gratuity_start_years === 3 ? 'selected' : ''}>3 Years (10%)</option>
                            <option value="4" ${company.gratuity_start_years === 4 ? 'selected' : ''}>4 Years (10%)</option>
                            <option value="5" ${company.gratuity_start_years === 5 ? 'selected' : ''}>5 Years (10%)</option>
                        </select>
                    </div>
                    
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: 'Update',
            confirmButtonColor: '#1e3a8a',
            cancelButtonColor: '#64748b',
            showLoaderOnConfirm: true,
            allowOutsideClick: () => !Swal.isLoading(),
            allowEscapeKey: () => !Swal.isLoading(),
            allowEnterKey: () => !Swal.isLoading(),
            customClass: {
                container: 'edit-company-modal',
                popup: 'edit-company-popup',
                title: 'edit-company-title',
                htmlContainer: 'edit-company-content',
                confirmButton: 'edit-company-confirm-btn',
                cancelButton: 'edit-company-cancel-btn',
                loader: 'edit-company-loader'
            },
            didOpen: () => {
                // Add event listener to ensure phone always has +263 prefix
                const phoneInput = document.getElementById('companyPhone');
                phoneInput.addEventListener('input', function(e) {
                    if (!this.value.startsWith('+263')) {
                        this.value = '+263' + this.value.replace(/^\+263/, '');
                    }
                });

                // Show/hide gratuity start years based on increment type
                const incrementTypeSelect = document.getElementById('gratuityIncrementType');
                const startYearsGroup = document.getElementById('gratuityStartYearsGroup');
                
                incrementTypeSelect.addEventListener('change', function() {
                    startYearsGroup.style.display = this.value === 'incremental' ? 'block' : 'none';
                });

                // Logo upload handling
                const logoInput = document.getElementById('companyLogo');
                const logoPreviewContainer = document.getElementById('logoPreviewContainer');
                const logoPreview = document.getElementById('logoPreview');
                const logoPlaceholder = document.getElementById('logoPlaceholder');
                const removeLogoBtn = document.getElementById('removeLogoBtn');
                let originalLogo = company.logo;

                // Handle logo click to trigger file input
                logoPreviewContainer.addEventListener('click', () => {
                    logoInput.click();
                });

                // Handle remove logo button
                if (removeLogoBtn) {
                    removeLogoBtn.addEventListener('click', (e) => {
                        e.stopPropagation();
                        if (logoPreview) {
                            logoPreview.remove();
                        }
                        if (logoPlaceholder) {
                            logoPlaceholder.style.display = 'flex';
                        }
                        removeLogoBtn.style.display = 'none';
                        logoInput.value = '';
                        originalLogo = null;
                    });
                }

                // Handle file selection
                logoInput.addEventListener('change', function(e) {
                    const file = e.target.files[0];
                    if (file) {
                        if (!file.type.startsWith('image/')) {
                            Swal.showValidationMessage('Please select an image file');
                            logoInput.value = '';
                            return;
                        }

                        const reader = new FileReader();
                        reader.onload = function(e) {
                            // Remove existing preview if any
                            if (logoPreview) {
                                logoPreview.remove();
                            }
                            if (logoPlaceholder) {
                                logoPlaceholder.style.display = 'none';
                            }

                            // Create and add new preview
                            const newPreview = document.createElement('img');
                            newPreview.src = e.target.result;
                            newPreview.alt = 'Company Logo';
                            newPreview.className = 'logo-preview';
                            newPreview.id = 'logoPreview';
                            newPreview.style.width = '100px';
                            newPreview.style.height = '100px';
                            logoPreviewContainer.insertBefore(newPreview, logoPreviewContainer.firstChild);

                            // Show remove button
                            if (removeLogoBtn) {
                                removeLogoBtn.style.display = 'flex';
                            }
                        };
                        reader.readAsDataURL(file);
                    }
                });
            },
            preConfirm: async () => {
                try {
                    // Show loading state immediately
                    Swal.showLoading();
                    
                    const name = document.getElementById('companyName').value;
                    const email = document.getElementById('companyEmail').value;
                    const phone_number = document.getElementById('companyPhone').value;
                    const city = document.getElementById('companyCity').value;
                    const address = document.getElementById('companyAddress').value;
                    const employee_id_prefix = document.getElementById('employeeIdPrefix').value;
                    const md_email = document.getElementById('mdEmail').value;
                    const gratuity_increment_type = document.getElementById('gratuityIncrementType').value;
                    const gratuity_start_years = document.getElementById('gratuityStartYears').value;
                    const logoFile = document.getElementById('companyLogo').files[0];
                
                    if (!name) {
                        Swal.hideLoading();
                        Swal.showValidationMessage('Please enter company name');
                        return false;
                    }
                    
                    // Validate email format
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (email && !emailRegex.test(email)) {
                        Swal.hideLoading();
                        Swal.showValidationMessage('Please enter a valid email address');
                        return false;
                    }
                    
                    if (md_email && !emailRegex.test(md_email)) {
                        Swal.hideLoading();
                        Swal.showValidationMessage('Please enter a valid MD email address');
                        return false;
                    }
                
                    // Validate phone format
                    if (phone_number && (!phone_number.startsWith('+263') || phone_number.length < 7)) {
                        Swal.hideLoading();
                        Swal.showValidationMessage('Please enter a valid phone number with +263 country code');
                        return false;
                    }
                
                    // Create FormData object
                    const formData = new FormData();
                    formData.append('name', name);
                    formData.append('email', email);
                    formData.append('phone_number', phone_number);
                    formData.append('city', city);
                    formData.append('address', address);
                    formData.append('employee_id_prefix', employee_id_prefix);
                    formData.append('md_email', md_email);
                    formData.append('gratuity_increment_type', gratuity_increment_type);
                    formData.append('gratuity_start_years', gratuity_start_years);
                    formData.append('is_active', company.is_active);
                    
                    if (logoFile) {
                        formData.append('logo', logoFile);
                    }

                    // Make the API call
                    const token = localStorage.getItem('token');
                    const response = await axios.put(
                        `${baseUrl}/api/companies/${company.id}/`, 
                        formData,
                        {
                            headers: { 
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'multipart/form-data'
                            }
                        }
                    );

                    if (response.status >= 200 && response.status < 300) {
                        // Fetch fresh data from the server
                        const freshDataResponse = await axios.get(`${baseUrl}/api/companies`, {
                            headers: {
                                'Authorization': `Bearer ${token}`
                            }
                        });
                        
                        // Update the companies state with fresh data
                        setCompanies(freshDataResponse.data);
                        
                        // Update selected company if it's the one being edited
                        if (selectedCompany && selectedCompany.id === company.id) {
                            const updatedCompany = freshDataResponse.data.find(c => c.id === company.id);
                            if (updatedCompany) {
                                setSelectedCompany(updatedCompany);
                            }
                        }

                        return true;
                    } else {
                        throw new Error(`Unexpected response status: ${response.status}`);
                    }
                } catch (error) {
                    console.error('Update error:', error);
                    Swal.hideLoading();
                    Swal.showValidationMessage(
                        error.response?.data?.message || 'Failed to update company'
                    );
                    return false;
                }
            }
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Company updated successfully',
                    confirmButtonColor: '#1e3a8a',
                    timer: 1500,
                    showConfirmButton: false
                });
            }
        });
    };

    const handleDeleteCompany = async (company, e) => {
        if (e) e.stopPropagation();
        
        const companyId = company.id;
        
        try {
            const result = await Swal.fire({
                title: 'Are you sure?',
                text: "You won't be able to revert this!",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#ef4444',
                cancelButtonColor: '#64748b',
                confirmButtonText: 'Yes, delete it!'
            });
            
            if (!result.isConfirmed) {
                return;
            }
            
            const token = localStorage.getItem('token');
            await axios.delete(`${baseUrl}/api/companies/${companyId}/`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            setCompanies(companies.filter(company => company.id !== companyId));
            
            // Close modal if the deleted company is currently selected
            if (selectedCompany && selectedCompany.id === companyId) {
                closeModal();
            }
            
            Swal.fire({
                icon: 'success',
                title: 'Deleted!',
                text: 'Company has been deleted.',
                confirmButtonColor: '#1e3a8a'
            });
        } catch (error) {
            console.log(error);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: 'Failed to delete company.',
                confirmButtonColor: '#1e3a8a'
            });
        }
    };

    const openCompanyDetails = (company) => {
        setSelectedCompany(company);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedCompany(null);
    };

    // Close modal if user clicks outside of it
    const handleModalBackdropClick = (e) => {
        if (e.target.classList.contains('company-modal-backdrop')) {
            closeModal();
        }
    };
        
  return (
        <div className="wrapper">
        <Topbar />
        <Sidebar />
            <div className="main-content">
            <div className="container">
                <div className="companies-header">
                    <div className="title-section">
                        <FaBuilding className="title-icon" />
        <h1>Companies</h1>
    </div>
                    <div className="search-filter-container">
                        <div className="search-box">
                            <FaSearch style={{color: '#1e3a8a', marginLeft: '10px'}} className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search companies..."
                                value={searchTerm}
                                onChange={handleSearch}
                            />
                        </div>
                    </div>
                </div>

                {loading && (
                    <div className="loading-spinner">
                        <div className="spinner"></div>
                        <p>Loading companies...</p>
                    </div>
                )}

                {error && (
                    <div className="error-message">
                        <p>Error: {error}</p>
                    </div>
                )}

                {!loading && !error && filteredCompanies.length > 0 && (
                    <div className="companies-grid">
                        {filteredCompanies.map((company) => (
                            <div 
                                key={company.id} 
                                className="company-card"
                                onClick={() => openCompanyDetails(company)}
                            >
                                <div className="company-card-header">
                                    <div className="company-logo-container">
                                        {company.logo ? (
                                            <img 
                                                src={`${baseUrl}${company.logo}`} 
                                                alt={`${company.name} logo`} 
                                                className="company-logo"
                                            />
                                        ) : (
                                            <div className="company-logo-placeholder">
                                                <FaBuilding />
                                            </div>
                                        )}
                                    </div>
                                    <div className="company-badges">
                                    <div className="company-status-badge">
                                        <span className={`status-indicator ${company.is_active ? 'active' : 'inactive'}`}>
                                            {company.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                        </div>
                                        <div className="company-subscription-badge">
                                            <span className={`subscription-indicator ${company.is_subscribed ? 'subscribed' : 'unsubscribed'}`}>
                                                {company.is_subscribed ? 'Subscribed' : 'Unsubscribed'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="company-details">
                                    <h3>{company.name}</h3>
                                </div>

                                <div className="company-actions" onClick={(e) => e.stopPropagation()}>
                                    <button className="edit-btn" onClick={(e) => handleEditCompany(company, e)}>
                                        <FaEdit /> Edit
                                    </button>
                                    <button className="delete-btn" onClick={(e) => handleDeleteCompany(company, e)}>
                                        <FaTrash /> Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {!loading && !error && filteredCompanies.length === 0 && (
                    <div className="no-results">
                        <FaBuilding className="no-results-icon" />
                        <p>No companies found</p>
                    </div>
                )}

                {showModal && selectedCompany && (
                    <div className="company-modal-backdrop" onClick={handleModalBackdropClick}>
                        <div className="company-modal">
                            <div className="company-modal-header">
                                <h2>{selectedCompany.name}</h2>
                                <button className="modal-close-btn" onClick={closeModal}>
                                    <FaTimes />
                                </button>
                            </div>
                            <div className="company-modal-content">
                                {!selectedCompany.is_active && (
                                    <div className="warning-banner inactive-warning">
                                        <FaTimesCircle className="warning-icon" />
                                        <p>This company is currently inactive</p>
                                    </div>
                                )}
                                {!selectedCompany.is_subscribed && (
                                    <div className="warning-banner subscription-warning">
                                        <FaCreditCard className="warning-icon" />
                                        <p>This company is not subscribed</p>
                                    </div>
                                )}
                                <div className="company-details-grid">
                                <div className="company-detail-item">
                                    <FaEnvelope className="detail-icon" />
                                    <div>
                                        <h4>Email</h4>
                                        <p>{selectedCompany.email || 'No email provided'}</p>
                                    </div>
                                </div>
                                <div className="company-detail-item">
                                    <FaPhone className="detail-icon" />
                                    <div>
                                        <h4>Phone</h4>
                                        <p>{selectedCompany.phone_number || 'No phone provided'}</p>
                                    </div>
                                </div>
                                    <div className="company-detail-item">
                                        <FaIdCard className="detail-icon" />
                                        <div>
                                            <h4>Registration Number</h4>
                                            <p>{selectedCompany.registration_number || 'Not provided'}</p>
                                        </div>
                                    </div>
                                <div className="company-detail-item">
                                    <FaCalendarAlt className="detail-icon" />
                                    <div>
                                        <h4>Registration Date</h4>
                                        <p>{new Date(selectedCompany.date_registered).toLocaleDateString() || 'Not available'}</p>
                                    </div>
                                </div>
                                <div className="company-detail-item">
                                    <FaBuilding className="detail-icon" />
                                    <div>
                                        <h4>Address</h4>
                                        <p>{selectedCompany.address || 'No address provided'}</p>
                                        </div>
                                    </div>
                                    <div className="company-detail-item">
                                        <FaCity className="detail-icon" />
                                        <div>
                                            <h4>City</h4>
                                            <p>{selectedCompany.city || 'Not provided'}</p>
                                        </div>
                                    </div>
                                    <div className="company-detail-item">
                                        <FaHashtag className="detail-icon" />
                                        <div>
                                            <h4>Employee ID Prefix</h4>
                                            <p>{selectedCompany.employee_id_prefix || 'Not set'}</p>
                                        </div>
                                    </div>
                                    <div className="company-detail-item">
                                        <FaPercent className="detail-icon" />
                                        <div>
                                            <h4>Gratuity Base Rate</h4>
                                            <p>{selectedCompany.gratuity_base_rate ? `${selectedCompany.gratuity_base_rate}%` : 'Not set'}</p>
                                        </div>
                                    </div>
                                    <div className="company-detail-item">
                                        <FaClock className="detail-icon" />
                                        <div>
                                            <h4>Gratuity Start Years</h4>
                                            <p>{selectedCompany.gratuity_start_years || 'Not set'}</p>
                                        </div>
                                    </div>
                                    <div className="company-detail-item">
                                        <FaPercent className="detail-icon" />
                                        <div>
                                            <h4>Gratuity Increment Type</h4>
                                            <p>{selectedCompany.gratuity_increment_type || 'Not set'}</p>
                                        </div>
                                    </div>
                                    <div className="company-detail-item">
                                        <FaUserTie className="detail-icon" />
                                        <div>
                                            <h4>MD Email</h4>
                                            <p>{selectedCompany.md_email || 'Not provided'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="company-modal-footer">
                                <button 
                                    className={`modal-status-btn ${selectedCompany.is_subscribed ? 'deactivate' : 'activate'}`}
                                    onClick={() => handleToggleStatus(selectedCompany)}
                                >
                                    {selectedCompany.is_subscribed ? 
                                        <><FaToggleOff /> Cancel Subscription</> : 
                                        <><FaToggleOn /> Activate Subscription</>
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            </div>
        </div>
    );
};


export default Companies;